import * as angular from 'angular';
import { Operation } from 'diffj/dist/lib/jsonPatch';
import { combineLatest, from, Observable, Subject, Subscription } from 'rxjs';
import { concatMap, filter, first, map, switchMap, takeUntil, takeWhile, tap, withLatestFrom } from 'rxjs/operators';

import { ChartActions, ChartSwitchActions } from '@mode/charts/domain';
import { ofType } from '@mode/legacy/util-domain';
import type { StateService } from '@uirouter/angularjs';
import { MixableBaseController } from '~/common/component';
import { BuildEmbedReportSelectors } from '~/selectors/embed-state/embed-state.selectors';
import { SelectedReportSelectors } from '~/selectors/selected-report.selectors';
import { AppStore } from '~/types/main.types';

import { EncodingHelpers } from '@mode/charts/util-vegas';
import { reportFilterActions, ReportFilterFacade } from '@mode/report-viewer/domain';
import { VirtualScrollFacade } from '@mode/reports/domain';
import {
  EmbedActionType,
  EmbedBuilderActions,
  EmbedReportActions,
  EmitMessages,
  FilterPanelActions,
  RpcMessages,
} from '@mode/reports/sandbox/contract';
import { LegacyAssetService, SortSettingsTypes } from '@mode/shared/contract-common';
import { SortSettingsModalDialogService } from '@mode/shared/ui';
import { ModalService } from '@mode/shared/ui-angularjs';
import { dispatchEvent, toDictionary, MetaTags, MetaTagsService, setIFrameSrc } from '@mode/shared/util-js';
import { FeatureFlag, FeatureFlagsFacade } from '@mode/shared/contract-common';
import { recordEvent } from '@mode/shared/util-tracing';
import { ReportFilterControl } from 'libs/shared/contract-common/src/lib/report';
import modalFilterPanelApplyConfirmation from '~/components/modal-filter-panel-apply-confirmation/modal-filter-panel-apply-confirmation.html';
import { Actions } from '../../actions';
import { ChartViewActionType } from '../../actions/chart-commands/view.actions';
import { CellActions } from '../../actions/notebooks/cell.actions';
import { ReportLayoutActions } from '../../actions/report-layout.actions';
import { VizTypes } from '../../common/types';
import { BugsnagHelpers } from '../../lib/bugsnag-helpers';
import * as reportPipes from '../../lib/report-iframe-pipes';
import { getReportBuilderUrl } from '../../reducers/selectors';
import { ChartViewService } from '../../services/chart-view.service';
import ViewService from '../../services/view-service';
import template from './report-builder.html';

const RPC_TIMEOUT_MS = 30000;

class BuilderIFrameTimeoutError extends Error {
  constructor() {
    super('Loading builder iframe timed out');
    Object.setPrototypeOf(this, BuilderIFrameTimeoutError.prototype);
  }
}

class ReportBuilderController extends MixableBaseController {
  static $inject = [
    '$window',
    '$element',
    '$scope',
    '$state',
    'RxStoreService',
    'actions$',
    'ViewService',
    'AssetService',
    'ModalService',
    'ReportFilterFacade',
    'VirtualScrollFacade',
    'ChartViewService',
    'MetaTagsService',
    'FeatureFlagsFacade',
    'SortSettingsModalDialogService',
  ];

  private iframeElement: HTMLIFrameElement;
  private report;
  private enabled = false;
  private iframeTimeout: number;
  private iframeRetryTimer: number;
  private watchedTokensSub: Subscription | undefined;

  embedPreviewPath: string;

  private blockSameOrigin$: Observable<boolean>;
  private sandboxAttrs$: Observable<string>;
  private reportFilters: ReportFilterControl[];

  constructor(
    private $window: angular.IWindowService,
    private $element: angular.IAugmentedJQuery,
    private $scope: angular.IScope,
    private $state: StateService,
    private store: AppStore,
    private actions$: Observable<Actions>,
    private viewService: ViewService,
    private assetService: LegacyAssetService,
    private modal: ModalService,
    private reportFilterFacade: ReportFilterFacade,
    private virtualScrollFacade: VirtualScrollFacade,
    private chartViewService: ChartViewService, // for effects
    private metaTags: MetaTagsService,
    private featureFlagsFacade: FeatureFlagsFacade,
    private sortSettingsDialogService: SortSettingsModalDialogService
  ) {
    super();
    this.blockSameOrigin$ = this.featureFlagsFacade.asObservable(FeatureFlag.IFrameBlockSameOrigin);
    this.embedPreviewPath = this.assetService.embedPreviewPathNg;
  }

  $onInit() {
    this.iframeElement = this.$element.find('iframe[name=mode-embed-frame]')[0] as HTMLIFrameElement;

    this.actions$
      .pipe(
        ofType(ReportLayoutActions.Type.ReloadFrame),
        withLatestFrom(this.store.select(getReportBuilderUrl)),
        takeUntil(this.destroyed$)
      )
      .subscribe(([, url]) => {
        // TODO: update once state has the theme url and selector can get the preview path
        const fullUrl = this.embedPreviewPath + url + this.debugParam();
        this.startIFrameLoad(fullUrl);
        BugsnagHelpers.leaveBreadcrumbSafely('Frame Reload', { url: fullUrl });
      });

    this.sandboxAttrs$ = combineLatest([
      this.blockSameOrigin$,
      this.featureFlagsFacade.asObservable(FeatureFlag.IFrameAllowForms),
      this.featureFlagsFacade.asObservable(FeatureFlag.IFrameAllowPopupEscape),
    ]).pipe(
      map(([blockSameOrigin, allowForms, allowPopupEscape]) => {
        const attrs: string[] = [];
        if (!blockSameOrigin) {
          attrs.push('allow-same-origin');
        }

        if (allowForms) {
          attrs.push('allow-forms');
        }

        if (allowPopupEscape) {
          attrs.push('allow-popups-to-escape-sandbox');
        }

        return attrs.join(' ');
      })
    );

    this.reportFilterFacade.applyFilterConfirmation$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(({ fields, confirm }) => {
        this.openFiltersConfirmModal(fields, confirm);
      });

    this.watchedTokensSub = this.chartViewService.getWatchChartTokens$().subscribe({
      next(/*tokens*/) {
        // noop. dispatching requests is managed in the service
      },
    });

    this.reportFilterFacade.filterControls$.pipe(takeUntil(this.destroyed$)).subscribe((data) => {
      this.reportFilters = data;
    });
  }

  openFiltersConfirmModal(filters: readonly string[], confirm: () => void): void {
    const modalAttributes = {
      filters,
      confirmApply: confirm,
    };

    const modalOptions = {
      template: modalFilterPanelApplyConfirmation,
    };

    this.modal.open(modalAttributes, modalOptions);
  }

  startIFrameLoad(url: string) {
    this.clearTimers();
    setIFrameSrc(this.iframeElement, url);

    // attempt retry once if necessary
    this.iframeRetryTimer = this.$window.setTimeout(() => {
      setIFrameSrc(this.iframeElement, url);
      BugsnagHelpers.leaveBreadcrumbSafely('Retry Frame Load', { url });
    }, RPC_TIMEOUT_MS / 3.0);

    // Log an error if the iframe never responds
    this.iframeTimeout = this.$window.setTimeout(() => {
      BugsnagHelpers.notifySafely(new BuilderIFrameTimeoutError(), (event) => {
        event.groupingHash = 'BuilderIFrameTimeoutError';
        event.addMetadata('iframe', {
          src: url,
        });
      });
    }, RPC_TIMEOUT_MS);
  }

  clearTimers() {
    this.$window.clearTimeout(this.iframeTimeout);
    this.$window.clearTimeout(this.iframeRetryTimer);
  }

  $onDestroy() {
    this.clearTimers();
    this.watchedTokensSub?.unsubscribe();
    this.watchedTokensSub = undefined;
  }

  $onChanges(changes: angular.IOnChangesObject) {
    if (changes.view) {
      const view = changes.view.currentValue;
      if (!view.notebookShown && !view.queryMode && !view.noPreviewRefresh) {
        if (!this.enabled) {
          this.enabled = true;
          this.initFrame();
        }
      }
    }
  }

  initFrame() {
    // Refer to the comments in report-viewer.component.ts initFrame() for more context.
    // The main difference in the builder is that since it doesn't need to worry about
    // fullscreen refreshes, it doesn't need to maintain an RPC queue.

    // First get the iframe bundle and feature flags
    combineLatest([
      from(import(/* webpackChunkName: "iframe-transport" */ '@mode/reports/sandbox/util-transport')),
      this.store.select(getReportBuilderUrl),
      this.blockSameOrigin$,
    ])
      .pipe(
        first() // since we only need it once, easier to do this than to handle cleanup
      )
      .subscribe(
        ([imports, url, blockSameOrigin]) => {
          const { Rpc, Emit } = imports;

          let subscription: Subscription;
          const diffProducer$ = new Subject<Operation[]>();
          const diffConsumer$ = diffProducer$.pipe(
            reportPipes.bufferPatches(),
            concatMap((delta) => {
              const currentSub = subscription;
              return Rpc.sendRequest(
                this.$window,
                this.iframeElement.contentWindow,
                new RpcMessages.UpdateState(delta)
              ).catch((err) => {
                // If the subscription has changed while waiting for a timeout, that probably
                // means a refresh happened and we can ignore this error.
                if (currentSub === subscription) {
                  BugsnagHelpers.notifySafely(err, (event) => {
                    event.groupingHash = 'ReportBuilderPatchQueueRpcTimeout';

                    event.addMetadata('debugInfo', {
                      patchCount: delta.length,
                      blockSameOrigin,
                      iframeMounted: this.$window.document.body.contains(this.iframeElement),
                    });
                  });
                }
              });
            }),
            takeUntil(this.destroyed$)
          );

          const getLayout = reportPipes.getLayout(
            this.store.selectNoEmpty(SelectedReportSelectors.getLayout),
            this.store.selectNoEmpty(SelectedReportSelectors.getTheme)
          );

          // Wait for ready message from iframe, and then init the report and start sending state updates
          Emit.getMessageStream(this.$window)
            .pipe(
              reportPipes.waitReady(),
              tap(() => {
                // try to stop the current queue to avoid sending unnecessary messages
                if (subscription != null) {
                  subscription.unsubscribe();
                }
                subscription = diffConsumer$.subscribe();

                this.clearTimers();
                BugsnagHelpers.leaveBreadcrumbSafely('IFrame ready received');
              }),
              getLayout,
              switchMap(({ layout, theme }) =>
                this.featureFlagsFacade.allFlags$.pipe(
                  first(),
                  map((flags) => ({ flags, layout, theme }))
                )
              ),
              switchMap(({ layout, theme, flags }) =>
                // Send layout and theme information first so the embed side can get the DOM ready
                Rpc.sendRequest(
                  this.$window,
                  this.iframeElement.contentWindow,
                  new RpcMessages.InitReport(layout, theme.href, theme.customSource, flags)
                )
              ),
              reportPipes.generatePatch(this.store.select(BuildEmbedReportSelectors.embedBuilderState)),
              takeWhile(() => this.iframeElement.contentWindow != null),
              reportPipes.reportError(url),
              takeUntil(this.destroyed$)
            )
            .subscribe((delta) => {
              diffProducer$.next(delta);
            });

          // Setup the action handling stream from the iframe
          Emit.getMessageStream(this.$window)
            .pipe(
              filter((msg) => msg.type === EmitMessages.MessageTypes.Action),
              map((msg) => (msg.type === EmitMessages.MessageTypes.Action ? msg : null)),
              takeUntil(this.destroyed$)
            )
            .subscribe((msg) => {
              this.checkActionBlockers(msg.action).then((blocked) => {
                if (!blocked) {
                  const action = this.convertBuilderAction(msg.action);
                  if (action != null) {
                    this.$scope.$applyAsync(() => this.store.dispatch(action));
                  } else {
                    this.executeAction(msg.action);
                  }
                }
              });
            });

          // TODO: update once state has the theme url and selector can get the preview path
          const fullUrl = this.embedPreviewPath + url + this.debugParam();
          BugsnagHelpers.leaveBreadcrumbSafely('Initial Frame Load', { url: fullUrl });
          this.startIFrameLoad(fullUrl);
        },
        (err) => BugsnagHelpers.notifySafely(err)
      );
  }

  // To simplify conversion from report-builder. Ideally, these would all be done via actions instead.
  executeAction(action: EmbedBuilderActions.Actions) {
    switch (action.type) {
      case EmbedReportActions.Type.DocumentClick:
        this.$element.click();
        break;
      case EmbedReportActions.Type.DocumentKeydown:
        if (action.keyCode === 13 || action.keyCode === 83) {
          if (action.metaKey || action.ctrlKey) {
            dispatchEvent<KeyboardEvent>(this.$element[0], 'keydown', {
              which: action.keyCode,
              keyCode: action.keyCode,
              key: action.key,
              metaKey: action.metaKey,
              ctrlKey: action.ctrlKey,
            });
          }
        }
        break;
      case EmbedBuilderActions.Type.UpdateV1View:
        this.viewService.updateVizSettings(`${action.vizType}_${action.token}`, action.view);
        break;
      case EmbedBuilderActions.Type.SelectQuery:
        this.viewService.selectQuery({ token: action.queryToken }, true);
        break;
      case EmbedBuilderActions.Type.AddChart:
        this.viewService.addChartToLayout(action.token);
        break;
      case EmbedActionType.MuzeChartRendered:
        break;
      case EmbedReportActions.Type.ReportStartRender:
        this.virtualScrollFacade.builderSetVirtualEnabled(action.virtualized);
        break;
    }
  }

  // A bit hacky. Ideal solution would make RemoveViz an async action so the modal could be a side-effect.
  checkActionBlockers(action: EmbedBuilderActions.Actions): PromiseLike<boolean> {
    return new Promise((resolve, reject) => {
      switch (action.type) {
        case EmbedBuilderActions.Type.RemoveViz:
          if (action.vizType === VizTypes.Text) {
            const confirmModal = this.modal.open(
              {
                headerText: 'Remove Text',
                confirmText: 'Are you sure you want to remove this text? This action cannot be undone.',
                buttonText: 'Remove',
              },
              {
                template: `
                  <div class="confirm-modal">
                    <mode-legacy-dialog-header (closed)="$dismiss()">
                      {{ headerText }}
                    </mode-legacy-dialog-header>
                    <div class="confirm-modal-container">
                      <p ng-bind-html="confirmText"></p>
                    </div>
                    <mode-legacy-dialog-footer>
                      <capra-button
                        text="Cancel"
                        variant="secondary"
                        (clicked)="$dismiss()"
                      ></capra-button>
                      <capra-button
                        type="submit"
                        [text]="buttonText || 'OK'"
                        (clicked)="$close(true)"
                      ></capra-button>
                    </mode-legacy-dialog-footer>
                  </div>
                `,
              }
            );

            confirmModal.result
              .then(function () {
                resolve(false);
              })
              .catch(function () {
                resolve(true);
              });
          } else if (action.vizType === VizTypes.Python) {
            this.store.dispatch({
              type: CellActions.Type.RemoveFromLayoutViaBuilder,
              vizToken: action.token,
            });

            resolve(false);
          } else {
            resolve(false);
          }
          break;
        case EmbedBuilderActions.Type.UpdateSortSettings:
          this.sortSettingsDialogService.open({
            encoding: action.event.encoding,
            selectedColumn: action.event.selectedColumn,
            descending: action.event.descending,
            applyAction: (sorts: SortSettingsTypes.Sort[]) => {
              action.event.encResult = sorts.reduce(
                (encResult, sort) =>
                  EncodingHelpers.addTableSort(encResult.encoding, sort.selected.value, sort.descending, false),
                EncodingHelpers.clearSorts(action.event.encoding)
              );
              resolve(false);
            },
            cancelAction: () => {
              resolve(true);
            },
          });
          break;
        default:
          resolve(false);
          break;
      }
    });
  }

  // This may be moved to another file for improved organization
  convertBuilderAction(action: EmbedBuilderActions.Actions): Actions {
    switch (action.type) {
      case EmbedBuilderActions.Type.EditViz:
        this.$state.go('application.reports.edit.viz', { vizToken: action.token });
        break;
      case EmbedBuilderActions.Type.RemoveViz:
        return {
          type: ReportLayoutActions.Type.RemoveViz,
          token: action.token,
          vizType: action.vizType,
        };
      case EmbedBuilderActions.Type.SetRowHeight:
        return {
          type: ReportLayoutActions.Type.SetRowHeight,
          token: action.token,
          vizType: action.vizType,
          height: action.height,
        };
      case EmbedBuilderActions.Type.SetFullTable:
        return new ReportLayoutActions.SetFullTable(action.token);
      case EmbedBuilderActions.Type.SetAutoTable:
        return new ReportLayoutActions.SetAutoTable(action.token);
      case EmbedBuilderActions.Type.UpdateTitle:
        switch (action.vizType) {
          case VizTypes.Python:
            return {
              type: CellActions.Type.UpdateVizTitle,
              vizToken: action.token,
              title: action.title,
            };
          case VizTypes.Chart:
            return ChartSwitchActions.updateDesignerChartTitle({
              token: action.token,
              title: action.title,
            });
        }
        break;
      case EmbedBuilderActions.Type.UpdateCaptionInfo:
        switch (action.vizType) {
          case VizTypes.Chart:
            return ChartSwitchActions.updateCaption({
              token: action.token,
              caption: action.captionInfo,
            });
        }
        break;
      case EmbedBuilderActions.Type.UpdateFormat:
        return ChartSwitchActions.updateFormat({
          token: action.token,
          format: action.format,
        });
        break;
      case EmbedBuilderActions.Type.GetDataProfiling:
        return ChartSwitchActions.getDataProfiling({
          token: action.token,
          cols: action.cols,
          reportFilters: this.reportFilters,
          referenceId: action.referenceId,
        });
        break;
      case EmbedBuilderActions.Type.UpdateBigValueDescription:
        return ChartSwitchActions.updateBigValueDescription({
          token: action.token,
          description: action.description,
        });

      // Add Viz
      case EmbedBuilderActions.Type.CloneViz:
        if (action.vizType === VizTypes.Chart) {
          return new ReportLayoutActions.CloneChart(action.token, action.position);
        }
        break;
      case EmbedBuilderActions.Type.CreateChart:
        return new ReportLayoutActions.CreateChart(action.queryToken, action.position);
      case EmbedBuilderActions.Type.AddTable:
        return new ReportLayoutActions.AddViz(action.token, null, VizTypes.Table, action.position, null);
      case EmbedBuilderActions.Type.AddText:
        return new ReportLayoutActions.CreateText(action.position);

      // Text
      case EmbedBuilderActions.Type.UpdateText:
        return new ReportLayoutActions.UpdateText(action.token, action.text);
      case EmbedBuilderActions.Type.ToggleTextMargin:
        return new ReportLayoutActions.ToggleTextMargins(action.token);

      // Drag/Drop/Resize
      case EmbedBuilderActions.Type.ResizeGridElement:
        return new ReportLayoutActions.ResizeGridElement(action.id, action.direction, action.columnSize);
      case EmbedBuilderActions.Type.MoveGridElement:
        return new ReportLayoutActions.MoveGridElement(action.id, action.row, action.column);

      // Report Filters
      case FilterPanelActions.Type.ApplyFilters:
        recordEvent('report-builder', 'filter-apply', { filters: action.filters });
        return reportFilterActions.requestApplyReportFilters({
          selected: toDictionary(action.filters, (f) => f.selected),
        });
      case FilterPanelActions.Type.ResetFilters:
        recordEvent('report-builder', 'filter-reset-all', { filters: action.filters });
        return reportFilterActions.resetReportFilters({
          defaults: toDictionary(action.filters, (f) => f.default),
        });
      case FilterPanelActions.Type.ResetFilter:
        recordEvent('report-builder', 'filter-reset', { filters: action.filter });
        return reportFilterActions.resetReportFilter({
          token: action.filter.token,
          defaults: action.filter.default,
        });
      case FilterPanelActions.Type.DeleteFilter:
        recordEvent('report-builder', 'filter-delete', {
          filter: action.filter,
        });
        return reportFilterActions.destroyReportFilter({ token: action.filter.token });
      case FilterPanelActions.Type.SetFilterType:
        recordEvent('report-builder', 'filter-set-type', {
          filter: action.filter,
          filterType: action.filterType,
          defaultSelection: action.defaultSelection,
        });
        return reportFilterActions.updateReportFilterType({
          filterType: action.filterType,
          selection: action.filter.selected,
          controlType: action.filter.controlType,
          token: action.filter.token,
          defaultSelection: action.defaultSelection,
          applyFilters: action.applyFilters,
        });
      case FilterPanelActions.Type.SetSelectionControlType:
        return reportFilterActions.updateReportFilter({
          filter: {
            ...action.filter,
            controlType: action.controlType,
          },
        });
      case FilterPanelActions.Type.SetAsDefault:
        recordEvent('report-builder', 'filter-set-as-default', {
          filters: action.filters,
        });
        return reportFilterActions.requestUpdateReportFilterDefaults({
          selected: toDictionary(action.filters, (f) => f.selected),
        });
      case FilterPanelActions.Type.SetRangeIncludeNulls:
        recordEvent('report-builder', 'filter-set-range-include-nulls', {
          filter: action.filter,
          includeNulls: action.includeNulls,
        });
        return reportFilterActions.updateReportFilter({
          filter: action.filter,
        });
      case FilterPanelActions.Type.SetHideSelectAll:
        recordEvent('report-builder', 'filter-hide-select-all', {
          filter: action.filter,
          selection: action.selection,
          hide: action.hideSelectAll,
        });
        return reportFilterActions.hideSelectAll({
          token: action.filter.token,
          hide: action.hideSelectAll,
          selection: action.selection,
          persist: action.persist,
        });
      case FilterPanelActions.Type.ShowRelevantValues:
        recordEvent('report-builder', 'show-relevant-values', {
          filter: action.filterToken,
          showRelevantValues: action.showRelevantValues,
        });
        return reportFilterActions.showRelevantValues({
          token: action.filterToken,
          showRelevantValues: action.showRelevantValues,
        });
      case FilterPanelActions.Type.MoveFilter:
        recordEvent('report-builder', 'filter-move', {
          filter: action.filter,
          next: action.nextToken,
        });
        return reportFilterActions.updateReportFilterPosition({
          destination: action.nextToken,
          source: action.filter.token,
        });
      case FilterPanelActions.Type.MoveFilterToBottom:
        recordEvent('report-builder', 'filter-move-bottom', {
          filter: action.filter,
        });
        return reportFilterActions.updateReportFilterPosition({
          destination: null,
          source: action.filter.token,
        });
      case FilterPanelActions.Type.FilterSelectionChange:
        recordEvent('report-builder', 'filter-selection-change', { token: action.token });
        return reportFilterActions.filterSelectionChange({
          token: action.token,
          selection: action.selection,
        });

      // Flamingo
      case EmbedReportActions.Type.SetTableOffset:
        return ChartActions.setOffset({
          token: action.token,
          offset: action.offset,
        });

      case EmbedReportActions.Type.SetTableColumnSearch:
        return ChartActions.setTableColumnSearch({
          token: action.token,
          columnSearchModel: action.columnSearchModel,
        });

      case EmbedReportActions.Type.UpdateEncoding:
        return {
          type: ChartViewActionType.UpdateEncoding,
          chartToken: action.token,
          result: action.encResult,
        };

      case EmbedReportActions.Type.UpdateVizIntersections:
        this.virtualScrollFacade.builderSetInViewport(action.intersections);
        break;
      case EmbedBuilderActions.Type.UpdateSortSettings:
        return {
          type: ChartViewActionType.UpdateEncoding,
          chartToken: action.token,
          result: action.event.encResult,
        };
    }

    return null;
  }

  private debugParam() {
    const debugjs = this.metaTags.getTag(MetaTags.DebugJS);
    return debugjs ? `&${MetaTags.DebugJS}=${debugjs}` : '';
  }
}

export const ReportBuilderComponent: angular.IComponentOptions = {
  bindings: {
    view: '<',
    report: '<',
  },
  template: template,
  controller: ReportBuilderController,
  controllerAs: 'ctrl',
};
